import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Tabs as TabsComponent } from '@Sage/hoi-poi-ui';

import './styles.scss';

const propTypes = {
    content: PropTypes.array,
    tabControlled: PropTypes.number,
    onChange: PropTypes.func,
    className: PropTypes.string,
    showScrollButtons: PropTypes.string, //'on' or 'off'
    tabsHeaderClass: PropTypes.string,
    tabsClassesObj: PropTypes.object,
    tabClassesObj: PropTypes.object,
    isWidget: PropTypes.bool,
    withScroll: PropTypes.bool,
};

const Tabs = ({
    content,
    className,
    onChange,
    tabControlled,
    tabsHeaderClass,
    tabsClassesObj,
    tabClassesObj,
    isWidget,
    withScroll, //for scroll padding
}) => {
    const [state, setState] = useState({
        tabOpened: 0,
        finalContent: [],
    });

    const renderTitle = useCallback((tab) => {
        if (!tab.count) return tab.title;
        return (
            <span className="fm-tabs__tab__title">
                {tab.title}
                <span className="fm-tabs__counter">({tab.count})</span>
            </span>
        );
    }, []);

    useEffect(() => {
        // Add the key prop to the tabs schema for compatibility with the hoi-poi component
        const finalContent = content.map((item, index) => {
            const finalItem = { ...item };
            finalItem.title = renderTitle(item);
            if (!finalItem?.key) finalItem.key = index.toString();
            return finalItem;
        });
        setState((state) => ({ ...state, finalContent }));
    }, [content, renderTitle]);

    const selectTab = useCallback((value) => {
        setState((state) => ({ ...state, tabOpened: value }));
    }, []);

    const handleOnChange = useCallback(
        (value) => {
            const finalValue = state.finalContent.findIndex((obj) => obj.key === value.toString());
            (onChange && onChange(null, finalValue)) || selectTab(finalValue);
        },
        [onChange, selectTab, state.finalContent],
    );

    const renderTabs = useMemo(() => {
        const { tabOpened, finalContent } = state;
        const value = onChange ? tabControlled : tabOpened;

        // Build classes object for hoi-poi-ui Tabs component
        let hoiPoiClasses = {
            root: 'fm-tabs-root',
            navContainer: 'fm-tabs-nav-container',
            nav: 'fm-tabs-nav',
            tab: 'fm-tab-root',
            tabActive: 'fm-tab-selected',
            inkBar: 'fm-tabs-indicator',
            navArrowBase: 'fm-tabs-scroll-button',
            content: 'fm-tabs-content',
            tabPane: 'fm-tabs-tab-pane',
        };

        // Apply custom classes from props if provided
        if (tabsClassesObj) {
            if (tabsClassesObj.root) hoiPoiClasses.root += ` ${tabsClassesObj.root}`;
            if (tabsClassesObj.navContainer)
                hoiPoiClasses.navContainer += ` ${tabsClassesObj.navContainer}`;
            if (tabsClassesObj.nav) hoiPoiClasses.nav += ` ${tabsClassesObj.nav}`;
            if (tabsClassesObj.indicator) hoiPoiClasses.inkBar += ` ${tabsClassesObj.indicator}`;
            if (tabsClassesObj.scrollButton)
                hoiPoiClasses.navArrowBase += ` ${tabsClassesObj.scrollButton}`;
        }

        if (tabClassesObj) {
            if (tabClassesObj.root) hoiPoiClasses.tab += ` ${tabClassesObj.root}`;
            if (tabClassesObj.selected) hoiPoiClasses.tabActive += ` ${tabClassesObj.selected}`;
        }

        // Single tab styling
        if (content.length === 1) {
            hoiPoiClasses.root += ' fm-tabs-single-tab';
        }

        // Build header class names
        let tabsHeaderClassNames = ['fm-tabs-header'];
        if (tabsHeaderClass) tabsHeaderClassNames.push(tabsHeaderClass);

        if (!withScroll && isWidget) tabsHeaderClassNames.push('is-widget no-scroll');
        if (isWidget) tabsHeaderClassNames.push('is-widget');
        if (withScroll) tabsHeaderClassNames.push('with-scroll');

        return (
            <div className={tabsHeaderClassNames.join(' ')}>
                <TabsComponent
                    classes={hoiPoiClasses}
                    activeKey={finalContent[value]?.key}
                    onChange={handleOnChange}
                    tabs={finalContent}
                />
            </div>
        );
    }, [
        content.length,
        handleOnChange,
        isWidget,
        onChange,
        state,
        tabClassesObj,
        tabControlled,
        tabsClassesObj,
        tabsHeaderClass,
        withScroll,
    ]);

    return <div className={`fm-tabs ${className || ''}`}>{renderTabs}</div>;
};

Tabs.propTypes = propTypes;

export default memo(Tabs);
